<template>
    <div>
        <card-header title="Edit Add-On" icon="fa-pencil"/>

        <entity-tabs v-model="tab" main-label="Add-On" main-icon="fa-pencil" :task-count="taskCount" :task-completed-count="taskCompleted" :note-count="noteCount"/>

        <card-body>
            <entity-tasks-pane v-show="tab === 'tasks'" :card="card" :child="child" entity-type="reg-addons" :entity-id="props.addonId" @count="taskCount = $event" @completed="taskCompleted = $event"/>
            <entity-notes-pane v-show="tab === 'notes'" :card="card" :child="child" entity-type="reg-addons" :entity-id="props.addonId" @count="noteCount = $event"/>
            <div v-show="tab === 'main'">
            <card-list>
                <registration-list-item :card="card" :registration="registration"/>
                <container-list-item padded v-if="notFound">
                    <i class="fas fa-exclamation-triangle mr-2"></i>This add-on could not be loaded. It may have already been removed from this registration.
                </container-list-item>
                <container-list-item padded v-if="orphaned">
                    <i class="fas fa-exclamation-triangle mr-2"></i><strong>This add-on no longer exists.</strong>
                    The trip add-on<template v-if="addon.override_title"> "{{ addon.override_title }}"</template> was deleted, but this registration still references it.
                    Use the button below to remove it from this registration.
                </container-list-item>
            </card-list>
            <addon-form v-if="!orphaned && !notFound" :addon="addon" :template="template"/>
            </div>
        </card-body>

        <card-footer class="pt-3 pb-3" v-show="tab === 'main'">
            <div class="columns">
                <div class="column" v-if="!notFound">
                    <b-button type="is-warning is-outlined" size="is-small" @click="deleteAddon"><i class="far fa-trash-alt"></i><span class="ml-2" v-if="confirmDelete">Remove Add-On?</span><span class="ml-2" v-else-if="orphaned">Remove From Registration</span></b-button>
                </div>
                <div class="column has-text-right" v-if="!orphaned && !notFound">
                    <b-button type="is-primary" size="is-small" @click="submit"><i class="fas fa-check mr-2"></i>Save Add-On</b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "../../TIER/components/CardHeader";
    import CardList from "../../TIER/components/CardList";
    import CardToolbar from "../../TIER/components/CardToolbar";
    import SearchListItem from "../../TIER/components/SearchListItem";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem";
    import SeparatorListItem from "../../TIER/components/SeparatorListItem";
    import NavigationListItem from "../../TIER/components/NavigationListItem";
    import TripListItem from "../../components/TripListItem";

    import {client as http} from '../../http_client';
    import CardBody from "../../TIER/components/CardBody";
    import DataRow from "../../TIER/components/DataRow";
    import DayListItem from "../../components/DayListItem";
    import EventListItem from "../../components/EventListItem";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import AddonForm from "./AddonForm";
    import CardFooter from "../../TIER/components/CardFooter";
    import RegistrationListItem from "@/components/RegistrationListItem";

    import async from "async";
    import EntityTabs from "@/components/EntityTabs";
    import EntityTasksPane from "@/components/EntityTasksPane";
    import EntityNotesPane from "@/components/EntityNotesPane";

    export default {
        name: "Users",
        props: ['card', 'child', 'props'],
        components: {EntityNotesPane, EntityTasksPane, EntityTabs, RegistrationListItem, CardFooter, AddonForm, ContainerListItem, EventListItem, DayListItem, DataRow, CardBody, TripListItem, NavigationListItem, SeparatorListItem, SubheaderListItem, SearchListItem, CardToolbar, CardList, CardHeader},
        data: function() {
            return {
                addon: {},
                template: {},
                trip: {},
                registration: {
                    trip: {},
                    user: {},
                    tasks: {},
                    addons: [],
                    flights: []
                },
                confirmDelete: false,
                dirty: null,
                orphaned: false,
                notFound: false,
                tab: 'main',
                taskCount: 0,
                taskCompleted: 0,
                noteCount: 0
            }
        },
        methods: {
            loadAddon: function() {
                this.$emit('loading', true);
                this.orphaned = false;
                this.notFound = false;
                async.series([
                    (cb) => {
                        http.get('/api/registrations/' + this.props.registrationId + '/addons/' + this.props.addonId, {force: true}).then(response => {
                            this.addon = response.data;
                            cb();
                        }).catch(cb);
                    },
                    (cb) => {
                        http.get('/api/registrations/' + this.props.registrationId, {}).then(response => {
                            this.registration = response.data;
                            cb();
                        }).catch(cb);
                    },
                    (cb) => {
                        http.get('/api/addons/' + this.addon.addon_id, {}).then(response => {
                            this.template = response.data;
                            cb();
                        }).catch(err => {
                            if (err.response && err.response.status === 404) {
                                // The trip add-on was deleted out from under this registration
                                this.orphaned = true;
                                cb();
                            } else {
                                cb(err);
                            }
                        });
                    }
                ], err => {
                    if (err) this.notFound = true;
                    this.$emit('loading', false);
                });
            },
            submit: function() {
                this.$emit('loading', true);
                http.put('/api/registrations/' + this.props.registrationId + '/addons/' + this.props.addonId, {
                    override_title: this.addon.override_title,
                    override_price: this.addon.override_price,
                    override_price_double: this.addon.override_price_double
                }, {force: true}).then(response => {
                    this.$reloadCard('addons', {registrationId: this.addon.registration_id});
                    this.$reloadCard('registration', {registrationId: this.addon.registration_id});
                    this.$reloadCard('registrations', {});
                    this.$saveAlert(false);
                    this.$closeCard(this.card);
                }).catch(err => {
                    this.$emit('loading', false);
                });
            },
            deleteAddon() {
                if (this.confirmDelete) {
                    this.$emit('loading', true);
                    http.delete('/api/registrations/' + this.props.registrationId + '/addons/' + this.props.addonId).then(response => {
                        this.$reloadCard('addons', {registrationId: this.addon.registration_id});
                        this.$reloadCard('registration', {registrationId: this.addon.registration_id});
                        this.$reloadCard('registrations', {});
                        this.$saveAlert(false);
                        this.$closeCard(this.card);
                    }).catch(err => {
                        this.$emit('loading', false);
                    });
                } else {
                    this.confirmDelete = true;
                    setTimeout(() => {
                        this.confirmDelete = false;
                    }, 3000);
                }
            }
        },
        watch: {
            'props.addonId': function () {
                this.dirty = null;
                this.loadAddon();
            },
            'addon': {
                deep: true,
                handler: function () {
                    this.dirty = (this.dirty !== null);
                    if (!this.$TIER.saveAlert.active && this.dirty)
                        this.$saveAlert(true, 'Are you done editing this add-on? Unsaved changes will be lost.');
                }
            }
        },
        mounted() {
            this.loadAddon();
        }
    };
</script>

<style lang="scss" scoped>

</style>
